import { queryBuilderColors } from 'bones/styles/colors';

interface Color {
  id: string;
  color: string;
}

class ColorManager {
  private usedColorIdsByIri: Record<string, Color>;

  private irisByColorId: Record<string, string[]>;

  constructor(
    priorColorsByIri: Record<
      string,
      { iri: string; color: string; colorId: string }
    > = {}
  ) {
    this.usedColorIdsByIri = {};
    this.irisByColorId = {};
    Object.values(priorColorsByIri).forEach(({ color, colorId: id, iri }) => {
      this.claimColor({ id, color }, iri);
    });
  }

  claimColor(color: Color, iri: string) {
    this.usedColorIdsByIri[iri] = color;
    this.irisByColorId[color.id] = this.irisByColorId[color.id] || [];
    this.irisByColorId[color.id].push(iri);
  }

  getColorForIri(iri: string) {
    // Reuse colors that we've already assigned
    if (this.usedColorIdsByIri[iri]) {
      return this.usedColorIdsByIri[iri];
    }

    // We want to pick colors in vizColor's order; start at zero
    let index = 0;
    let color = queryBuilderColors[index];

    let minCollisionsCount = Infinity;
    let minCollisionsIndex = 0;

    // Try to find the next color that isn't in use
    while (this.irisByColorId[color.id] !== undefined) {
      // Note whether this color has the fewest collisions that we've seen
      const collisions = this.irisByColorId[color.id].length;
      if (collisions < minCollisionsCount) {
        minCollisionsCount = collisions;
        minCollisionsIndex = index;
      }

      // We already know this color has at least one collision; try the next color...
      index += 1;
      color = queryBuilderColors[index];

      if (index >= queryBuilderColors.length) {
        // All colors are in use; pick the color that had the least
        // number of collisions
        color = queryBuilderColors[minCollisionsIndex];
        break;
      }
    }

    // Note that we've now assigned this color before we return it
    this.claimColor(color, iri);
    return color;
  }

  countIrisAssignedPerColor() {
    const histogram: Record<string, number> = {};
    Object.values(this.irisByColorId).forEach((iriList) => {
      const bin = String(iriList.length);
      histogram[bin] = (histogram[bin] || 0) + 1;
    });
    return histogram;
  }
}

export { ColorManager };
